<template>
  <a-layout class="main-layout" data-testid="layout-shell">
    <!-- Header Component -->
    <LayoutHeader v-model:collapsed="collapsed" />

    <!-- Layout -->
    <a-layout class="layout-body">
      <!-- Sidebar -->
      <a-layout-sider
        v-model:collapsed="collapsed"
        :width="200"
        :collapsed-width="64"
        class="layout-sider"
        data-testid="layout-sidebar"
      >
        <a-menu
          v-model:selected-keys="selectedKeys"
          v-model:open-keys="openKeys"
          mode="inline"
          class="side-menu"
          aria-label="主导航"
          data-testid="side-menu"
        >
          <a-menu-item
            v-for="item in menuItems"
            :key="item.key"
            :data-menu-key="item.key"
            @click="navigateTo(item.routeName)"
          >
            <template #icon>
              <component :is="item.icon" />
            </template>
            {{ item.label }}
          </a-menu-item>
        </a-menu>
      </a-layout-sider>

      <!-- Main Content -->
      <a-layout-content class="layout-content" data-testid="layout-content">
        <div class="content-wrapper" data-testid="layout-content-wrapper">
          <router-view />
        </div>
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Component } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useLayoutStore } from "@/stores/layout";
import { useStudyContextStore } from "@/stores/studyContext";
import { getDataExportStatus } from "@/api/admin-data-export.api";
import LayoutHeader from "./Header.vue";
import IconDashboard from "@arco-design/web-vue/es/icon/icon-dashboard";
import IconBulb from "@arco-design/web-vue/es/icon/icon-bulb";
import IconSettings from "@arco-design/web-vue/es/icon/icon-settings";
import IconBook from "@arco-design/web-vue/es/icon/icon-book";
import IconExperiment from "@arco-design/web-vue/es/icon/icon-experiment";
import IconApps from "@arco-design/web-vue/es/icon/icon-apps";
import IconUserGroup from "@arco-design/web-vue/es/icon/icon-user-group";
import IconUser from "@arco-design/web-vue/es/icon/icon-user";
import IconSafe from "@arco-design/web-vue/es/icon/icon-safe";
import IconFile from "@arco-design/web-vue/es/icon/icon-file";
import IconIdcard from "@arco-design/web-vue/es/icon/icon-idcard";
import IconDownload from "@arco-design/web-vue/es/icon/icon-download";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const layoutStore = useLayoutStore();
const studyContextStore = useStudyContextStore();
layoutStore.ensureHydrated();

// State
type MainMenuKey =
  | "dashboard"
  | "organizations"
  | "dictionary"
  | "studies"
  | "sites"
  | "studyRoles"
  | "studyMembers"
  | "participants"
  | "crfVersions"
  | "settings"
  | "systemConfig"
  | "dataExport";

interface MenuItem {
  key: MainMenuKey;
  label: string;
  icon: Component;
  routeName: MainRouteName;
  adminOnly?: boolean;
}

const allMenuItems: MenuItem[] = [
  {
    key: "dashboard",
    label: "仪表板",
    icon: IconDashboard,
    routeName: "Dashboard",
  },
  {
    key: "organizations",
    label: "组织管理",
    icon: IconBulb,
    routeName: "Organizations",
  },
  {
    key: "dictionary",
    label: "词典管理",
    icon: IconBook,
    routeName: "DictionaryVersions",
  },
  {
    key: "studies",
    label: "研究管理",
    icon: IconExperiment,
    routeName: "Studies",
  },
  { key: "sites", label: "研究中心", icon: IconApps, routeName: "Sites" },
  {
    key: "studyRoles",
    label: "研究角色",
    icon: IconUserGroup,
    routeName: "StudyRoles",
  },
  {
    key: "studyMembers",
    label: "研究团队",
    icon: IconUser,
    routeName: "StudyMembers",
  },
  {
    key: "participants",
    label: "参与者管理",
    icon: IconIdcard,
    routeName: "Participants",
  },
  {
    key: "crfVersions",
    label: "CRF 管理",
    icon: IconFile,
    routeName: "CrfVersionManagement",
  },
  {
    key: "settings",
    label: "系统设置",
    icon: IconSettings,
    routeName: "Settings",
  },
  {
    key: "systemConfig",
    label: "配置管理",
    icon: IconSafe,
    routeName: "SystemConfig",
    adminOnly: true,
  },
  {
    key: "dataExport",
    label: "数据导出",
    icon: IconDownload,
    routeName: "AdminDataExport",
    adminOnly: true,
  },
];

const dataExportMenuVisible = ref(true);
let dataExportStatusRequestId = 0;

const refreshDataExportVisibility = async () => {
  const requestId = ++dataExportStatusRequestId;
  const studyId = studyContextStore.currentStudyId;
  if (!studyId) {
    dataExportMenuVisible.value = false;
    return;
  }

  try {
    const response = await getDataExportStatus({ studyId });
    if (requestId === dataExportStatusRequestId) {
      dataExportMenuVisible.value = response.data.enabled !== false;
    }
  } catch (error) {
    console.error("Failed to load data export menu visibility:", error);
    if (requestId === dataExportStatusRequestId) {
      dataExportMenuVisible.value = true;
    }
  }
};

// Filter menu items based on user role
const menuItems = computed(() => {
  const isSuperUser = authStore.user?.role === "SUPER";
  return allMenuItems.filter((item) => {
    if (item.adminOnly && !isSuperUser) return false;
    if (item.key === "dataExport" && !dataExportMenuVisible.value) return false;
    return true;
  });
});

const selectedKeys = ref<MainMenuKey[]>([]);
const openKeys = ref<string[]>([]);

const collapsed = computed({
  get: () => layoutStore.isSidebarCollapsed,
  set: (value: boolean) => layoutStore.setSidebarCollapsed(value),
});

// authStore is used by Header component

// Methods
type MainRouteName =
  | "Dashboard"
  | "Organizations"
  | "DictionaryVersions"
  | "Studies"
  | "Sites"
  | "StudyRoles"
  | "StudyMembers"
  | "Participants"
  | "CrfVersionManagement"
  | "Settings"
  | "SystemConfig"
  | "AdminDataExport";

const navigateTo = (routeName: MainRouteName) => {
  if (route.name === routeName) return;
  router.push({ name: routeName });
};

// Update selected keys based on current route
const routeNameToMenuKey: Record<string, MainMenuKey> = {
  Dashboard: "dashboard",
  Organizations: "organizations",
  OrganizationDetail: "organizations",
  DictionaryVersions: "dictionary",
  DictionaryVersionDetail: "dictionary",
  Studies: "studies",
  StudyDetail: "studies",
  Sites: "sites",
  StudyRoles: "studyRoles",
  StudyMembers: "studyMembers",
  Participants: "participants",
  ParticipantDetail: "participants",
  CrfVersionManagement: "crfVersions",
  CrfMapping: "crfVersions",
  Settings: "settings",
  SystemConfig: "systemConfig",
  AdminDataExport: "dataExport",
};

const updateSelectedKeys = () => {
  const routeName = route.name ? String(route.name) : "";
  const mappedKey = routeNameToMenuKey[routeName];
  if (mappedKey) {
    selectedKeys.value = [mappedKey];
  } else {
    selectedKeys.value = [];
  }
};

// Watch for collapsed state changes to update menu
watch(
  () => collapsed.value,
  (newValue) => {
    if (newValue) {
      // When sidebar is collapsed, close all open keys
      openKeys.value = [];
    }
  },
);

watch(
  () => route.fullPath,
  () => {
    updateSelectedKeys();
  },
  { immediate: true },
);

watch(
  () => studyContextStore.currentStudyId,
  () => {
    refreshDataExportVisibility();
  },
  { immediate: true },
);
</script>

<style scoped>
.main-layout {
  --layout-header-height: 64px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-1);
}

.layout-body {
  flex: 1;
  display: flex;
  min-height: 0;
  background: transparent;
}

.layout-sider {
  background: #fff;
  border-right: 1px solid var(--color-border);
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease;
  position: sticky;
  top: var(--layout-header-height);
  height: calc(100vh - var(--layout-header-height));
  overflow-y: auto;
}

.side-menu {
  border-right: none;
}

.layout-content {
  flex: 1;
  background: #fff;
  min-width: 0;
  height: calc(100vh - var(--layout-header-height));
  overflow: auto;
}

.content-wrapper {
  padding: 24px;
  min-height: 100%;
  box-sizing: border-box;
}

/* Dark theme styles - using stronger selectors and !important */
:global([data-theme="dark"]) .main-layout .layout-sider {
  background: var(--color-bg-2) !important;
  border-right-color: var(--color-border-2) !important;
}

:global([data-theme="dark"]) .main-layout .side-menu {
  background: transparent !important;
}

:global([data-theme="dark"]) .main-layout .side-menu .arco-menu-item {
  color: var(--color-text-2) !important;
  background: transparent !important;
}

:global([data-theme="dark"]) .main-layout .side-menu .arco-menu-item:hover {
  background: var(--color-fill-2) !important;
  color: var(--color-text-1) !important;
}

:global([data-theme="dark"]) .main-layout .side-menu .arco-menu-item-selected {
  background: var(--color-fill-3) !important;
  color: var(--color-primary) !important;
}

:global([data-theme="dark"]) .main-layout .side-menu .arco-menu-icon {
  color: var(--color-text-3) !important;
}

:global([data-theme="dark"])
  .main-layout
  .side-menu
  .arco-menu-item-selected
  .arco-menu-icon {
  color: var(--color-primary) !important;
}

:global([data-theme="dark"]) .main-layout .layout-content {
  background: var(--color-bg-2) !important;
}

/* Global dark theme for Arco Design components */
:global([data-theme="dark"]) .arco-layout-sider {
  background: var(--color-bg-2) !important;
  border-right-color: var(--color-border-2) !important;
}

:global([data-theme="dark"]) .arco-menu {
  background: transparent !important;
  color: var(--color-text-2) !important;
}

:global([data-theme="dark"]) .arco-menu-item {
  background: transparent !important;
  color: var(--color-text-2) !important;
}

:global([data-theme="dark"]) .arco-menu-item:hover {
  background: var(--color-fill-2) !important;
  color: var(--color-text-1) !important;
}

:global([data-theme="dark"]) .arco-menu-item-selected {
  background: var(--color-fill-3) !important;
  color: var(--color-primary) !important;
}

:global([data-theme="dark"]) .arco-menu-icon {
  color: var(--color-text-3) !important;
}

:global([data-theme="dark"]) .arco-menu-item-selected .arco-menu-icon {
  color: var(--color-primary) !important;
}
</style>
